import ky, { isHTTPError, isTimeoutError } from 'ky';
import type { Hooks, Input, ResponsePromise } from 'ky';

import { captureException } from '@eaphone/sentry';
import { Auth } from '@eaphone/storage';

import type { CustomOptions } from './type';

const statusErrors: Record<number, string> = {
  400: '发送的请求参数不正确',
  401: '登录超时，请重新登录',
  403: '没有访问该功能的权限',
  404: '找不到您要访问的内容',

  // 人为触发，只是利用其编号：
  408: '网络连接超时，请重试',
  415: '返回的数据格式不正确',
  418: '未知错误，请求失败',
  421: '服务暂时不可用',
} as const;

/** 错误预设生成 */
export class KyError extends Error {
  code: number;

  info: {
    message?: string;
    errCode?: number | string;
  };

  constructor(
    code = 418,
    message = statusErrors[code] ?? statusErrors[418],
    rest = {},
  ) {
    super(message);
    this.code = code;
    this.name = 'KyError';
    this.info = {
      message,
      ...rest,
    };
  }
}

function mergeErrorJson({
  errcode,
  errCode,
  errmsg,
  errMsg,
  error,
  errorMsg,
  message,
  success,
  ...rest
}: {
  errcode?: number;
  errCode?: number;
  errmsg?: string;
  errMsg?: string;
  error?: boolean | string;
  errorMsg?: string;
  message?: string;
  success?: boolean;
} = {}) {
  return {
    ...rest,
    errCode: errCode ?? errcode,
    error: success === false || error === true || Boolean(error),
    message:
      message ||
      errorMsg ||
      errMsg ||
      errmsg ||
      (typeof error === 'string' ? error : ''),
  };
}

async function toJson(response: Response) {
  return response.clone().json().then(mergeErrorJson);
}

function typeCheck(type: string) {
  return (response: Response) => {
    const ContentType = response.headers.get('Content-Type');

    return ContentType && ContentType.startsWith(type);
  };
}

const isJson = typeCheck('application/json');

const beforeRequest: Hooks['beforeRequest'] = [
  async function autoAddToken({
    request,
    options,
  }: {
    request: Request;
    options: CustomOptions;
  }) {
    if (options.auth !== false) {
      // 请求前检测 token，通过 auth: false 可关闭此功能。
      const { token } = Auth;

      if (token) {
        // 默认检测 token 是否存在，存在则在请求头中添加 token。
        request.headers.set('Authorization', `Bearer ${token}`);
      } else {
        Auth.clear();
        // token 不存在时，取消请求并报错。
        throw new KyError(401);
      }
    }
  },
];

function haveResponse(request: Request, response: Response) {
  return (
    response.ok &&
    !['DELETE', 'HEAD'].includes(request.method) &&
    response.status !== 204 &&
    response.body !== null
  );
}

const afterResponse: Hooks['afterResponse'] = [
  async function handleCustomError({ request, response }) {
    if (haveResponse(request, response) && isJson(response)) {
      const {
        error = false,
        message,
        errCode,
        ...rest
      } = await toJson(response);

      if (message === 'invalid token') {
        // fork 401
        Auth.clear();
        throw new KyError(401);
      }

      if (error) {
        // 检查 error 或 success 字段
        throw new KyError(418, message, {
          errCode,
          ...rest,
        });
      }
    }
  },
];

const beforeError: Hooks['beforeError'] = [
  async function handleHttpError({ error }) {
    if (error.name === 'KyError') {
      // 智能错误信息
      throw error;
    }

    if (isHTTPError(error)) {
      if (error.response.status === 401) {
        // 返回401时删除本地Token
        Auth.clear();
      }

      // 抛出 http 错误
      if (error.response.body !== null && isJson(error.response)) {
        const { message, errCode } = await toJson(error.response);

        if (message === 'invalid token' || errCode === 5) {
          // fork 401
          Auth.clear();

          return new KyError(401);
        }

        return new KyError(error.response.status, message);
      }

      return new KyError(error.response.status);
    }

    if (isTimeoutError(error) || error.message === 'Failed to fetch') {
      return new KyError(408);
    }

    if (error.name === 'AbortError') {
      return new KyError(418, '请求已取消');
    }

    console.error(error);

    return new KyError(418);
  },
];

const KyBase = ky.extend({
  timeout: 10000,
  prefix: import.meta.env.API_BASE_URL, // .best-shot/env.toml
  retry: 0,
  hooks: { beforeRequest, afterResponse, beforeError },
});

export function KyPureError(error: Error): never {
  if (error.name === 'KyError') {
    // 智能错误信息
    throw error;
  }

  console.error({ error });

  captureException(error, {
    // 记录未知错误
    tags: { scene: 'KyErrorUnknown' },
  });

  throw new KyError(418);
}

type Methods = 'get' | 'post' | 'put' | 'patch' | 'delete';

export const Ky = new Proxy(
  {},
  {
    get(_, key: Methods) {
      return (input: Input, options: CustomOptions = {}) => {
        return KyBase[key](input, options);
      };
    },
  },
) as Record<
  Methods,
  <T = unknown>(url: Input, options?: CustomOptions) => ResponsePromise<T>
>;

export const KyJson = new Proxy(
  {},
  {
    get(_, key: Methods | 'file') {
      if (key === 'file') {
        return (
          input: string,
          { file, ...options }: CustomOptions & { file: File },
        ) => {
          const formData = new FormData();

          formData.append('file', file, file?.name);

          return KyJson.post(input, {
            ...options,
            body: formData,
          });
        };
      }

      if (['get', 'post', 'put', 'patch', 'delete'].includes(key)) {
        return async (input: Input, options: CustomOptions = {}) => {
          const caller = async () => {
            const instance = KyBase[key](input, options);

            return (key === 'delete' ? instance : instance.json())
              .then((io) =>
                io && typeof io === 'object' && 'data' in io && io.data
                  ? 'total_record' in io
                    ? { ...io, list: io.data, data: undefined }
                    : io.data
                  : io,
              )
              .catch(KyPureError);
          };

          return caller();
        };
      }
    },
  },
) as Record<
  Methods | 'file',
  <T = object>(resource: string, options?: CustomOptions) => Promise<T>
>;
